import { createSlice, createSelector, nanoid } from "@reduxjs/toolkit";
import { INTERVALS } from "../../utils/chart";
import { isOrderHistoryDeal } from "../../services/platform/normalizers";

const CHART_TIMEFRAMES = new Set(INTERVALS);

const getNewestValue = (item = {}) => {
  const values = [
    item.time_msc,
    item.timeMsc,
    item.time_update,
    item.timeUpdate,
    item.time_create,
    item.timeCreate,
    item.time_setup,
    item.timeSetup,
    item.time,
    item.deal,
    item.ticket,
    item.order,
  ];

  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number) && number > 0) return number;
  }
  return 0;
};

const sortNewestFirst = (items) => {
  items.sort((left, right) => getNewestValue(right) - getNewestValue(left));
  return items;
};

const initialState = {
  user: null,
  isLoggedIn: false,
  isInitialized: false,
  positions: [],
  pendingOrders: [],
  orderHistory: [],
  symbols: [],
  symbolsLoading: false,
  symbolsError: null,
  prices: [],
  pricesLoading: false,
  pricesError: null,
  timeframes: [...INTERVALS],
  socketStatus: "disconnected",
  icons: {},
  iconsLoading: false,
  iconsError: null,
  openOrdersLoading: false,
  openOrdersError: null,
  pendingOrdersLoading: false,
  pendingOrdersError: null,
  orderHistoryLoading: false,
  orderHistoryError: null,
  mostTradedSymbols: [],
  favourites: [],
  account: {
    login: null,
    balance: 0,
    equity: 0,
    credit: 0,
    margin: 0,
    marginFree: 0,
    marginLevel: 0,
  },
  chartLoading: false,
  isDark: (() => {
    try {
      const theme = localStorage.getItem("signal-core-theme");
      if (theme === null) return true;
      return theme === "dark";
    } catch (e) {
      return true;
    }
  })(),
  settings: (() => {
    try {
      const saved = localStorage.getItem("trading_settings");
      return saved
        ? JSON.parse(saved)
        : { confirmOpen: true, confirmClose: true, showNotifications: true };
    } catch (e) {
      return { confirmOpen: true, confirmClose: true, showNotifications: true };
    }
  })(),
  toasts: [],
};

const getIdentityValues = (item) => new Set(
  [
    item?.positionID,
    item?.positionId,
    item?.position,
    item?.position_id,
    item?.ticket,
    item?.order,
    item?.deal,
    item?.id,
    item?._id,
  ]
    .filter(value => value !== undefined && value !== null)
    .map(value => String(value).trim())
    .filter(value => value !== "" && value !== "0"),
);

const matchesOrderIdentity = (existing, incoming) => {
  const incomingIds = getIdentityValues(incoming);
  if (!incomingIds.size) return false;
  return [...getIdentityValues(existing)].some(value => incomingIds.has(value));
};

const getHistoryIdentity = (item) => (
  item?.ticket ||
  item?.deal ||
  item?.id ||
  item?._id ||
  item?.order ||
  item?.positionID ||
  item?.positionId ||
  item?.position ||
  item?.position_id
);

const platformSlice = createSlice({
  name: "platform",
  initialState,
  reducers: {
    setUser(state, a) {
      state.user = a.payload;
      if (a.payload) state.isLoggedIn = true;
    },
    setIsLoggedIn(state, a) {
      state.isLoggedIn = a.payload;
    },
    setInitialized(state, a) {
      state.isInitialized = a.payload;
    },
    setAccount(state, a) {
      state.account = { ...state.account, ...a.payload };
    },

    updateSetting(state, a) {
      const { key, value } = a.payload;
      state.settings[key] = value;
      localStorage.setItem("trading_settings", JSON.stringify(state.settings));
    },

    addToast(state, a) {
      const isDuplicate = state.toasts.some(
        (t) => t.message === a.payload.message,
      );
      if (isDuplicate) return;
      const id = nanoid();
      state.toasts.push({ id, ...a.payload });
    },
    removeToast(state, a) {
      state.toasts = state.toasts.filter((t) => t.id !== a.payload);
    },

    toggleTheme(state) {
      state.isDark = !state.isDark;
      localStorage.setItem(
        "signal-core-theme",
        state.isDark ? "dark" : "light",
      );
    },

    setSymbolsLoading(state, a) {
      state.symbolsLoading = a.payload;
    },
    setSymbols(state, a) {
      state.symbols = a.payload;
      state.symbolsLoading = false;
    },
    setSymbolsError(state, a) {
      state.symbolsError = a.payload;
      state.symbolsLoading = false;
    },

    setPricesLoading(state, a) {
      state.pricesLoading = a.payload;
    },
    setPrices(state, a) {
      state.prices = a.payload;
      state.pricesLoading = false;
    },
    setPricesError(state, a) {
      state.pricesError = a.payload;
      state.pricesLoading = false;
    },
    setTimeframes(state, a) {
      const timeframes = Array.isArray(a.payload)
        ? a.payload
          .map(value => String(value || "").toUpperCase())
          .filter(value => CHART_TIMEFRAMES.has(value))
        : [];
      if (timeframes.length) {
        state.timeframes = timeframes;
      }
    },
    setSocketStatus(state, a) {
      state.socketStatus = a.payload;
    },

    setIconsLoading(state, a) {
      state.iconsLoading = a.payload;
    },
    setIcons(state, a) {
      state.icons = a.payload;
      state.iconsLoading = false;
    },
    setIconsError(state, a) {
      state.iconsError = a.payload;
      state.iconsLoading = false;
    },

    setOpenOrdersLoading(state, a) {
      state.openOrdersLoading = a.payload;
    },
    setOpenOrders(state, a) {
      state.positions = Array.isArray(a.payload)
        ? sortNewestFirst([...a.payload])
        : [];
      state.openOrdersLoading = false;
    },
    setOpenOrdersError(state, a) {
      state.openOrdersError = a.payload;
      state.openOrdersLoading = false;
    },

    setPendingOrdersLoading(state, a) {
      state.pendingOrdersLoading = a.payload;
    },
    setPendingOrders(state, a) {
      state.pendingOrders = Array.isArray(a.payload)
        ? sortNewestFirst([...a.payload])
        : [];
      state.pendingOrdersLoading = false;
    },
    setPendingOrdersError(state, a) {
      state.pendingOrdersError = a.payload;
      state.pendingOrdersLoading = false;
    },

    setOrderHistoryLoading(state, a) {
      state.orderHistoryLoading = a.payload;
    },
    setOrderHistory(state, a) {
      state.orderHistory = Array.isArray(a.payload)
        ? sortNewestFirst(a.payload.filter(isOrderHistoryDeal))
        : [];
      state.orderHistoryLoading = false;
    },
    appendOrderHistory(state, a) {
      const incoming = Array.isArray(a.payload)
        ? a.payload.filter(isOrderHistoryDeal)
        : [];
      incoming.forEach(item => {
        const incomingId = getHistoryIdentity(item);
        const existingIndex = incomingId
          ? state.orderHistory.findIndex(existing => String(getHistoryIdentity(existing)) === String(incomingId))
          : -1;

        if (existingIndex >= 0) {
          state.orderHistory[existingIndex] = {
            ...state.orderHistory[existingIndex],
            ...item,
          };
        } else {
          state.orderHistory.push(item);
        }
      });
      sortNewestFirst(state.orderHistory);
      state.orderHistoryLoading = false;
    },
    setOrderHistoryError(state, a) {
      state.orderHistoryError = a.payload;
      state.orderHistoryLoading = false;
    },

    setMostTradedSymbols(state, a) {
      state.mostTradedSymbols = a.payload;
    },

    setChartLoading(state, a) {
      state.chartLoading = a.payload;
    },
    addOpenOrder(state, a) {
      const existing = state.positions.find((o) =>
        matchesOrderIdentity(o, a.payload),
      );
      if (existing) {
        Object.assign(existing, a.payload);
      } else {
        state.positions.unshift(a.payload);
      }
      sortNewestFirst(state.positions);
    },

    addPendingOrder(state, a) {
      const existing = state.pendingOrders.find((o) =>
        matchesOrderIdentity(o, a.payload),
      );
      if (existing) {
        Object.assign(existing, a.payload);
      } else {
        state.pendingOrders.unshift(a.payload);
      }
      sortNewestFirst(state.pendingOrders);
    },
    updateOpenOrder(state, a) {
      const position = state.positions.find((item) =>
        matchesOrderIdentity(item, a.payload),
      );
      // console.log("🚀 ~ platformSlice ~ updateOpenOrder ~ position:", position)
      if (position) {
        Object.assign(position, a.payload);
      } else {
        state.positions.unshift(a.payload);
      }
      sortNewestFirst(state.positions);
    },
    updatePendingOrder(state, a) {
      // console.log("🚀 ~ platformSlice ~ updatePendingOrder ~ a.payload:", a.payload)
      const order = state.pendingOrders.find((item) =>
        matchesOrderIdentity(item, a.payload),
      );
      if (order) {
        Object.assign(order, a.payload);
      } else {
        state.pendingOrders.unshift(a.payload);
      }
      sortNewestFirst(state.pendingOrders);
    },
    removeOpenOrder(state, a) {
      state.positions = state.positions.filter(
        (item) => !matchesOrderIdentity(item, a.payload),
      );
    },
    removePendingOrder(state, a) {
      state.pendingOrders = state.pendingOrders.filter(
        (item) => !matchesOrderIdentity(item, a.payload),
      );
    },
    updateOpenPositionProfits(state, a) {
      const updates = Array.isArray(a.payload) ? a.payload : [];
      updates.forEach(update => {
        const position = state.positions.find(item =>
          matchesOrderIdentity(item, update),
        );
        if (position) Object.assign(position, update);
      });
      sortNewestFirst(state.positions);
    },
    addOrderHistory(state, a) {
      const incomingId = getHistoryIdentity(a.payload);
      if (!isOrderHistoryDeal(a.payload)) {
        if (incomingId) {
          state.orderHistory = state.orderHistory.filter(
            item => String(getHistoryIdentity(item)) !== String(incomingId),
          );
        }
        return;
      }
      const existingIndex = incomingId
        ? state.orderHistory.findIndex(item => String(getHistoryIdentity(item)) === String(incomingId))
        : -1;

      if (existingIndex >= 0) {
        state.orderHistory[existingIndex] = {
          ...state.orderHistory[existingIndex],
          ...a.payload,
        };
        const [updated] = state.orderHistory.splice(existingIndex, 1);
        state.orderHistory.unshift(updated);
      } else {
        state.orderHistory.unshift(a.payload);
      }
      sortNewestFirst(state.orderHistory);
    },
    removeOrderHistory(state, a) {
      const incomingId = getHistoryIdentity(a.payload);
      if (!incomingId) return;
      state.orderHistory = state.orderHistory.filter(
        (item) => String(getHistoryIdentity(item)) !== String(incomingId),
      );
    },

    toggleFavourite(state, a) {
      const sym = a.payload;
      const idx = state.favourites.indexOf(sym);
      if (idx >= 0) state.favourites.splice(idx, 1);
      else state.favourites.push(sym);
    },
    addFavourite(state, a) {
      if (!state.favourites.includes(a.payload))
        state.favourites.push(a.payload);
    },
    removeFavourite(state, a) {
      state.favourites = state.favourites.filter((s) => s !== a.payload);
    },
    clearFavourites(state) {
      state.favourites = [];
    },
  },
});

export const {
  setUser,
  setIsLoggedIn,
  setInitialized,
  setAccount,
  setSymbolsLoading,
  setSymbols,
  setSymbolsError,
  setPricesLoading,
  setPrices,
  setPricesError,
  setTimeframes,
  setSocketStatus,
  setIconsLoading,
  setIcons,
  setIconsError,
  setOpenOrdersLoading,
  setOpenOrders,
  setOpenOrdersError,
  setPendingOrdersLoading,
  setPendingOrders,
  setPendingOrdersError,
  setOrderHistoryLoading,
  setOrderHistory,
  appendOrderHistory,
  setOrderHistoryError,
  setMostTradedSymbols,
  setChartLoading,
  addOpenOrder,
  addPendingOrder,
  updateOpenOrder,
  updatePendingOrder,
  removeOpenOrder,
  removePendingOrder,
  updateOpenPositionProfits,
  addOrderHistory,
  removeOrderHistory,
  toggleFavourite,
  addFavourite,
  removeFavourite,
  clearFavourites,
  updateSetting,
  addToast,
  removeToast,
  toggleTheme,
} = platformSlice.actions;

// Selectors
export const selectUser = (s) => s.platform.user;
export const selectIsLoggedIn = (s) => s.platform.isLoggedIn;
export const selectIsInitialized = (s) => s.platform.isInitialized;
export const selectFavourites = (s) => s.platform.favourites;
export const selectIsFavourite = (sym) =>
  createSelector(selectFavourites, (favs) => favs.includes(sym));
export const selectPositions = (s) => s.platform.positions;
export const selectPendingOrders = (s) => s.platform.pendingOrders;
export const selectAccount = (s) => s.platform.account;
export const selectIcons = (s) => s.platform.icons;
export const selectSymbols = (s) => s.platform.symbols;
export const selectSymbolsLoading = (s) => s.platform.symbolsLoading;
export const selectTimeframes = (s) => s.platform.timeframes;
export const selectSocketStatus = (s) => s.platform.socketStatus;
export const selectSettings = (s) => s.platform.settings;
export const selectToasts = (s) => s.platform.toasts;
export const selectIsDark = (s) => s.platform.isDark;

export default platformSlice.reducer;
